'use client';

import { expandAllFeature, hotkeysCoreFeature, propMemoizationFeature, selectionFeature, syncDataLoaderFeature } from '@headless-tree/core';
import { useTree } from '@headless-tree/react';
import { useCallback, useEffect, useState } from 'react';
import { gltfDataParser } from './gltfDataParser';
import { GltfTreeItem } from './GltfTreeItem';
import type { GltfTreeProps, IGltfTreeNode } from './types';
import { defaultHotKeyConfig } from '@/editor/components/tree/shared/config/customHotKeyConfig';
import { customClickBehaviorFeature } from '@/editor/components/tree/shared/features/customClickBehaviorFeature';

/** GLTF tree view component using headless-tree */
export const GltfTreeView = ({
  gltf,
  selectedNodeId,
  onNodeSelect,
  onTreeReady
}: GltfTreeProps) => {
  const [rootNode, setRootNode] = useState<IGltfTreeNode | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>(selectedNodeId ? [selectedNodeId] : []);

  // Helper function to collect all node IDs recursively
  const getAllNodeIds = useCallback((node: IGltfTreeNode): string[] => {
    const ids = [node.id];
    node.children.forEach(child => {
      ids.push(...getAllNodeIds(child));
    });
    return ids;
  }, []);

  // Update root node when GLTF changes
  useEffect(() => {
    if (!gltf) {
      setRootNode(null);
      setExpandedNodeIds([]);
      return;
    }
    const parsedNode = gltfDataParser.parseGltf(gltf);
    setRootNode(parsedNode);

    // Expand all nodes by collecting all node IDs recursively
    const allNodeIds = getAllNodeIds(parsedNode);
    setExpandedNodeIds(allNodeIds);

    // Notify parent that tree is ready
    if (onTreeReady) {
      onTreeReady(parsedNode);
    }
  }, [gltf, onTreeReady, getAllNodeIds]); // Only depend on gltf, not rootNode

  // Node lookup helper
  const findNodeById = useCallback((nodeId: string, node_0: IGltfTreeNode = rootNode!): IGltfTreeNode | null => {
    if (!node_0) return null;
    if (node_0.id === nodeId) return node_0;
    for (const child_0 of node_0.children) {
      const found = findNodeById(nodeId, child_0);
      if (found) return found;
    }
    return null;
  }, [rootNode]);
  const tree = useTree<IGltfTreeNode>({
    rootItemId: 'root',
    indent: 14,
    canReorder: false,
    state: {
      expandedItems: expandedNodeIds,
      selectedItems: selectedNodeIds
    },
    setExpandedItems: setExpandedNodeIds,
    setSelectedItems: (updaterOrValue: string[] | ((old: string[]) => string[])) => {
      const nodeIds = typeof updaterOrValue === 'function' ? updaterOrValue(selectedNodeIds) : updaterOrValue;
      setSelectedNodeIds(nodeIds);
      // Call selection callback with first selected node
      if (nodeIds.length > 0 && onNodeSelect) {
        const selectedNode = findNodeById(nodeIds[0]);
        if (selectedNode) {
          onNodeSelect(nodeIds[0], selectedNode);
        }
      }
    },
    getItemName: item => {
      return item.getItemData()?.name || '';
    },
    isItemFolder: item_0 => {
      const itemData = item_0.getItemData();
      return itemData ? itemData.children.length > 0 : false;
    },
    scrollToItem: item_1 => {
      item_1.getElement()?.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: 'smooth'
      });
    },
    hotkeys: {
      ...defaultHotKeyConfig<IGltfTreeNode>()
    },
    dataLoader: {
      getItem: (itemId: string): IGltfTreeNode => {
        if (itemId === 'root' && rootNode) {
          return rootNode;
        }
        return findNodeById(itemId) || rootNode!;
      },
      getChildren: (itemId_0: string): string[] => {
        if (!rootNode) return [];
        if (itemId_0 === 'root') {
          return rootNode ? [rootNode.id] : [];
        }
        const node_1 = findNodeById(itemId_0);
        return node_1?.children.map(child_1 => child_1.id) || [];
      }
    },
    canRename: () => false,
    // Disable renaming for GLTF tree
    canDrag: () => false,
    // Disable dragging for GLTF tree
    canDrop: () => false,
    // Disable dropping for GLTF tree
    features: [syncDataLoaderFeature, hotkeysCoreFeature, expandAllFeature, customClickBehaviorFeature, selectionFeature, propMemoizationFeature]
  });

  // Show empty state if no GLTF data
  if (!gltf || !rootNode) {
    return <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No GLTF data to display
      </div>;
  }
  return <div className="flex h-full w-full overflow-hidden bg-background text-2xs">
      <div className="flex-1 overflow-auto p-1" {...tree.getContainerProps()}>
        {tree.getItems().map(item_2 => <GltfTreeItem key={item_2.getId()} item={item_2} level={item_2.getItemMeta().level} isSelected={item_2.isSelected()} isExpanded={item_2.isExpanded()} isFocused={item_2.isFocused()} isFolder={item_2.isFolder()} renamingValue={undefined} />)}
      </div>
    </div>;
};
GltfTreeView.displayName = 'GltfTreeView';